import { create } from 'zustand';

import { api } from 'src/utils/axios';

import { MissionsRepository } from 'src/repositories/missions/missions.repository';

import {
  quizType,
  quizResult,
  checkInType,
  scanCountType,
  quizQuestionsType,
  carPlateDetailsType,
  missionsListItemType,
} from 'src/types/missions/missions';

type States = {
  isLoading: boolean;
  isLoadingMissionsList: boolean;
  isLoadingMissionDetails: boolean;
  isLoadingStartQuiz: boolean;
  isLoadingCheckIn: boolean;
  isLoadingQuestions: boolean;
  isLoadingFinishQuiz: boolean;
  isLoadingFinishInstant: boolean;
  isLoadingStartScanMission: boolean;
  isLoadingCarPlateDetails: boolean;
  isLoadingStartCarPlateMission: boolean;
  isLoadingRegisterCarPlate: boolean;
  isLoadingCarPlateResult: boolean;
  isLoadingCustomScanDetails: boolean;
  isLoadingCustomScanV2Details: boolean;
  isLoadingCustomScanV3Details: boolean;
  isLoadingCustomScanProgress: boolean;
  isLoadingStartCustomScanMission: boolean;
  isLoadingCustomScanV2Progress: boolean;
  isLoadingCustomScanV3Progress: boolean;
  isLoadingStartCustomScanV2: boolean;
  missionsList: missionsListItemType[];
  scanCountDetails: scanCountType | null;
  instantDetails: scanCountType | null;
  checkInDetails: checkInType | null;
  quizDetails: quizType | null;
  quizQuestions: quizQuestionsType | null;
  quizResult: quizResult | null;
  scanResultError: boolean;
  carPlateResultError: boolean;
  carPlateDetails: carPlateDetailsType | null;
  customScanDetails: any;
  customScanV2Details: any;
  customScanV3Details: any;
  customScanProgress: any;
  customScanV2Progress: any;
  customScanV3Progress: any;
};

type Actions = {
  getMissionsList: () => Promise<void>;
  getScanCountDetails: (id: string) => Promise<void>;
  getCheckInDetails: (id: string) => Promise<any>;
  checkIn: (id: string, location: number[]) => Promise<void>;
  getQuizDetails: (id: string) => Promise<any>;
  startQuiz: (id: string) => Promise<any>;
  finishQuiz: (id: string, answers: string[]) => Promise<void>;
  finishInstant: (id: string) => Promise<void>;
  startScanMission: (id: string) => Promise<void>;
  getCarPlateDetails: (id: string) => Promise<void>;
  startCarPlateMission: (id: string) => Promise<void>;
  registerCarPlate: (id: string, carPlate: string[]) => Promise<void>;
  getInstantDetails: (id: string) => Promise<void>;
  getCustomScanDetails: (id: string) => Promise<void>;
  getCustomScanProgress: (id: string) => Promise<any>;
  startCustomScanMission: (id: string) => Promise<void>;
  getCustomScanV2Details: (id: string) => Promise<void>;
  getCustomScanV3Details: (id: string) => Promise<void>;
  getCustomScanV2Progress: (id: string) => Promise<any>;
  getCustomScanV3Progress: (id: string) => Promise<any>;
  startCustomScanV2Mission: (id: string) => Promise<void>;
};

type Missions = Actions & States;

const initialValues = {
  isLoading: false,
  isLoadingMissionsList: false,
  isLoadingMissionDetails: false,
  isLoadingStartQuiz: false,
  isLoadingCheckIn: false,
  isLoadingQuestions: false,
  isLoadingFinishQuiz: false,
  isLoadingFinishInstant: false,
  isLoadingStartScanMission: false,
  isLoadingCarPlateDetails: false,
  isLoadingStartCarPlateMission: false,
  isLoadingRegisterCarPlate: false,
  isLoadingCarPlateResult: false,
  isLoadingCustomScanDetails: false,
  isLoadingCustomScanV2Details: false,
  isLoadingCustomScanV3Details: false,
  isLoadingCustomScanProgress: false,
  isLoadingStartCustomScanMission: false,
  isLoadingCustomScanV2Progress: false,
  isLoadingCustomScanV3Progress: false,
  isLoadingStartCustomScanV2: false,
  missionsList: [],
  scanCountDetails: null,
  instantDetails: null,
  checkInDetails: null,
  quizDetails: null,
  quizQuestions: null,
  quizResult: null,
  carPlateResultError: false,
  scanResultError: false,
  carPlateDetails: null,
  customScanDetails: null,
  customScanV2Details: null,
  customScanV3Details: null,
  customScanProgress: null,
  customScanV2Progress: null,
  customScanV3Progress: null,
};

const missionsRepository = new MissionsRepository(api);

export const useMissionsStore = create<Missions>((set: any, get: any) => ({
  ...initialValues,

  getMissionsList: async () => {
    set({ isLoadingMissionsList: true });
    const response = await missionsRepository.getMissionsList();
    if (response.isLeft()) {
      set({ isLoadingMissionsList: false });
      throw new Error(response.value.message);
    } else {
      set({ missionsList: response.value, isLoadingMissionsList: false });
    }
  },

  getInstantDetails: async (id) => {
    set({ isLoadingMissionDetails: true });
    const response = await missionsRepository.getMissionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingMissionDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ instantDetails: response.value, isLoadingMissionDetails: false });
      return response.value;
    }
  },

  getScanCountDetails: async (id) => {
    set({ isLoadingMissionDetails: true });
    const response = await missionsRepository.getMissionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingMissionDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ scanCountDetails: response.value, isLoadingMissionDetails: false });
      return response.value;
    }
  },

  getCheckInDetails: async (id) => {
    set({ isLoadingMissionDetails: true });
    const response = await missionsRepository.getMissionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingMissionDetails: false });
      return false;
    }
    set({ checkInDetails: response.value, isLoadingMissionDetails: false });
    return response.value;
  },

  getQuizDetails: async (id) => {
    set({ isLoadingMissionDetails: true });
    const response = await missionsRepository.getMissionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingMissionDetails: false });
      return false;
    }
    set({ quizDetails: response.value, isLoadingMissionDetails: false });
    return response.value;
  },

  startQuiz: async (missionId) => {
    set({ isLoadingStartQuiz: true });
    const response = await missionsRepository.startQuiz({ missionId });

    if (response.isLeft()) {
      set({ quizQuestions: null, isLoadingStartQuiz: false });
      return false;
    }
    set({ quizQuestions: response.value, isLoadingStartQuiz: false });
    return response.value;
  },

  checkIn: async (missionId, location) => {
    set({ isLoadingCheckIn: true });
    const response = await missionsRepository.checkIn({ missionId, location });

    if (response.isLeft()) {
      set({ isLoadingCheckIn: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCheckIn: false });
    }
  },

  finishQuiz: async (missionId, answers) => {
    set({ isLoadingFinishQuiz: true });
    const response = await missionsRepository.finishQuiz({ missionId, answers });

    if (response.isLeft()) {
      set({ quizResult: null, isLoadingFinishQuiz: false });
    } else {
      set({ quizResult: response.value, isLoadingFinishQuiz: false });
    }
  },

  startScanMission: async (missionId) => {
    set({ isLoadingStartScanMission: true });
    const response = await missionsRepository.startScanMission({ missionId });

    if (response.isLeft()) {
      if (response.value.message === 'Mission already started') {
        set({ scanResultError: false, isLoadingStartScanMission: false });
      } else {
        set({ scanResultError: true, isLoadingStartScanMission: false });
      }
    } else {
      set({ scanResultError: false, isLoadingStartScanMission: false });
    }
  },

  startCustomScanMission: async (missionId) => {
    set({ isLoadingStartCustomScanMission: true });
    const response = await missionsRepository.startCustomScanMission({ missionId });

    if (response.isLeft()) {
      if (response.value.message === 'Mission already started') {
        set({ scanResultError: false, isLoadingStartCustomScanMission: false });
      } else {
        set({ scanResultError: true, isLoadingStartCustomScanMission: false });
      }
    } else {
      set({ scanResultError: false, isLoadingStartCustomScanMission: false });
    }
  },

  getCarPlateDetails: async (id) => {
    set({ isLoadingCarPlateDetails: true });
    const response = await missionsRepository.getCarPlateDetails(id);

    if (response.isLeft()) {
      set({ isLoadingCarPlateDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ carPlateDetails: response.value, isLoadingCarPlateDetails: false });
    }
  },

  startCarPlateMission: async (missionId) => {
    set({ isLoadingStartCarPlateMission: true });
    const response = await missionsRepository.startCarPlateMission({ missionId });

    if (response.isLeft()) {
      if (response.value.message === 'Mission already started') {
        set({ carPlateResultError: false, isLoadingStartCarPlateMission: false });
        throw new Error(response.value.message);
      } else {
        set({ carPlateResultError: true, isLoadingStartCarPlateMission: false });
        throw new Error(response.value.message);
      }
    } else {
      set({ carPlateResultError: false, isLoadingStartCarPlateMission: false });
    }
  },

  registerCarPlate: async (missionId, carPlate) => {
    set({ isLoadingRegisterCarPlate: true });
    const response = await missionsRepository.registerCarPlate({ missionId, carPlate });

    if (response.isLeft()) {
      set({ isLoadingRegisterCarPlate: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingRegisterCarPlate: false });
    }
  },

  finishInstant: async (missionId) => {
    set({ isLoadingFinishInstant: true });
    const response = await missionsRepository.finishInstant({ missionId });

    if (response.isLeft()) {
      set({ isLoadingFinishInstant: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingFinishInstant: false });
    }
  },

  getCustomScanDetails: async (missionId) => {
    set({ isLoadingCustomScanDetails: true });
    const response = await missionsRepository.getCustomScanDetails(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ customScanDetails: response.value, isLoadingCustomScanDetails: false });
    }
  },

  getCustomScanProgress: async (missionId) => {
    set({ isLoadingCustomScanProgress: true });
    const response = await missionsRepository.getCustomScanProgress(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanProgress: false });
      return false;
    }
    set({ customScanProgress: response.value, isLoadingCustomScanProgress: false });
    return response.value;
  },

  getCustomScanV2Details: async (missionId) => {
    set({ isLoadingCustomScanV2Details: true });
    const response = await missionsRepository.getCustomScanV2Details(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanV2Details: false });
      throw new Error(response.value.message);
    } else {
      set({ customScanV2Details: response.value, isLoadingCustomScanV2Details: false });
    }
  },

  getCustomScanV2Progress: async (missionId) => {
    set({ isLoadingCustomScanV2Progress: true });
    const response = await missionsRepository.getCustomScanV2Progress(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanV2Progress: false });
      return false;
    }
    set({ customScanV2Progress: response.value, isLoadingCustomScanV2Progress: false });
    return response.value;
  },

  startCustomScanV2Mission: async (missionId) => {
    set({ isLoadingStartCustomScanV2: true });
    const response = await missionsRepository.startCustomScanV2Mission({ missionId });

    if (response.isLeft()) {
      if (response.value.message === 'Mission already started') {
        set({ scanResultError: false, isLoadingStartCustomScanV2: false });
      } else {
        set({ scanResultError: true, isLoadingStartCustomScanV2: false });
      }
    } else {
      set({ scanResultError: false, isLoadingStartCustomScanV2: false });
    }
  },

  getCustomScanV3Details: async (missionId) => {
    set({ isLoadingCustomScanV3Details: true });
    const response = await missionsRepository.getCustomScanV3Details(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanV3Details: false });
      throw new Error(response.value.message);
    } else {
      set({ customScanV3Details: response.value, isLoadingCustomScanV3Details: false });
    }
  },

  getCustomScanV3Progress: async (missionId) => {
    set({ isLoadingCustomScanV3Progress: true });
    const response = await missionsRepository.getCustomScanV3Progress(missionId);

    if (response.isLeft()) {
      set({ isLoadingCustomScanV3Progress: false });
      return false;
    }
    set({ customScanV3Progress: response.value, isLoadingCustomScanV3Progress: false });
    return response.value;
  },
}));
